import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import * as once from 'once';

import { UserModel, UserBusinessModel, UserRolesModel } from '@models/index';
import { RequestModel, BusinessModel } from '@models/index';
import * as fromRoot from '@reducers/index'
import { BusinessActions, UsersActions, ResellersActions, AppActions, AuthActions } from '@actions/index';
import { addRandomImageToBusiness } from '@utilities/function-utils';
import { DataDeletionRequestModel } from '@models/dataDeletionRequestModel.model';
import {Role} from '@enums/role.enum';

declare var profitwell: Function;
const startProfitwell = once((user_email: string) => profitwell('start', { user_email }));

/**
 * AppFacadeService  dispatches all needed actions
 * related to general app data and gets all the app storage 
 * information asociated with the respective selectors.
 */
@Injectable({
  providedIn: 'root'
})

export class AppFacadeService {
  /**
   * AppFacadeService Constructor
   * @param store App storage
   */
  constructor(private store: Store<any>) { }

  getErrorTrialOver$(): Observable<boolean> {
    return this.store.select(fromRoot.getErrorTrialOver)
  }

   /**
   * Dispatches the action to set the user inactivity time
   * <pre>
     * <code class="language-typescript">
     * setInactivityTime(60000)
        </code>
     * </pre>
   * @param time Inactivity time in miliseconds 
   */
  setInactivityTime(time: number) {
    this.store.dispatch(AppActions.setInactivityTime({ time }))
  }

  setErrorTrialOver() {
    this.store.dispatch(AppActions.setErrorTrialOver())
  }

  clearErrorTrialOver() {
    this.store.dispatch(AppActions.clearErrorTrialOver())
  }

  /** Dispatches the action to set the app state to 
   * indicate that a task is currently being executed (petition
   * is being processed)
   * <pre>
     * <code class="language-typescript">
     * executeTaskApp()
        </code>
     * </pre>
   * */
  executeTaskApp() {
    this.store.dispatch(AppActions.executeTask())
  }

  /** Dispatches the action set the state
   * to indicate that the petition has been
   * processed and there is a response
   * <pre>
     * <code class="language-typescript">
     * executeTaskAppSuccess()
        </code>
     * </pre>
   * */
  executeTaskAppSuccess() {
    this.store.dispatch(AppActions.executeTaskSuccess())
  }

  /**
   * Dispatches the action to set the app error
   * to app state
   * <pre>
     * <code class="language-typescript">
     * appError('This is an error example')
        </code>
     * </pre>
   * @param error The error to be set on the app state
   */
  appError(error: any, url?: string) {
    this.store.dispatch(AppActions.appError({ error, url }))
  }

  /** Dispatches the action to clear error on the app state 
   * <pre>
     * <code class="language-typescript">
     * clearError()
        </code>
     * </pre>
  */
  clearError() {
    this.store.dispatch(AppActions.clearError())
  }

  /** Dispatches the action to clear the app state
   * <pre>
     * <code class="language-typescript">
     * clearState()
        </code>
     * </pre>
   */
  clearState() {
    this.store.dispatch(AppActions.clearState())
  }

  /**
   * Dispatches the action to get all business. The
   * search is performed as a paginated search.
   * <pre>
     * <code class="language-typescript">
     * searchBusinesses({page: 0, size: 30})
        </code>
    * </pre>
   * @param payload Contains the properties to be sent
   * to the endpoint, where:
   * - page: is the page number.
   * - size: is the number of items per page.
   */
  searchBusinesses(payload: { page: number, size: number }) {
    const request: RequestModel = {
      params: `business?page=${payload.page}&size=${payload.size}`
    }
    this.store.dispatch(BusinessActions.searchBusinesses({ request }))
  }

  searcharchivedBusinessByQuery(payload: { page: number, size: number, query: string }) {
    const request: RequestModel = {
      params: `business/autocomplete-archived?page=${payload.page}&size=${payload.size}&query=${payload.query}`
    }
    this.store.dispatch(BusinessActions.searchArchivedBusinessesByQuery({ request }))
  }

  /**
   * Dispatches the action to get the businesses associated
   * with a specific reseller. The search is performed as a paginated 
   * search.
   * <pre>
     * <code class="language-typescript">
     * searchBusinessesByReseller({page: 0, size: 30, resellerId: '5e8388ba69254e3b7e931f3e'})
        </code>
    * </pre>
   * @param payload Contains the properties to be sent
   * to the endpoint, where:
   * - resellerId: is the unique id of the reseller to which the business belongs to
   * - page: is the page number.
   * - size: is the number of items per page.
   */
  searchBusinessesByReseller(payload: { page: number, size: number, resellerId: string }) {
    const request: RequestModel = {
      params: `resellers/businesses?id=${payload.resellerId}&page=${payload.page}&size=${payload.size}`
    }
    this.store.dispatch(BusinessActions.searchBusinesses({ request }))
  }

  /**
   * Dispatches the action to search business that matches an specific string
   * <pre>
     * <code class="language-typescript">
     * searchBusinessByQuery({page: 0, size: 30, query: 'get mor'})
        </code>
    * </pre>
   * @param payload Contains the properties to be sent
   * to the endpoint, where:
   * - page: is the page number.
   * - size: is the number of items per page.
   * - query: is the string that should be matched.
   */
  searchBusinessByQuery(payload: { page: number, size: number, query: string }) {
    const request: RequestModel = {
      params: `business/autocomplete?page=${payload.page}&size=${payload.size}&query=${payload.query}`
    }
    this.store.dispatch(BusinessActions.searchBusinessesByQuery({ request }))
  }

  /**
   * Dispatches the action to search businesses by a
   * reseller that match a specific string
   * <pre>
     * <code class="language-typescript">
     * searchBusinessByQueryByReseller({page: 0, size: 30, query: 'get mor', resellerId: '5e8388ba69254e3b7e931f3e'})
        </code>
    * </pre>
   * @param payload Contains the properties to be sent
   * to the endpoint, where: 
   * - resellerId: is the unique id of the reseller to which the business belongs to.
   * - page: is the page number.
   * - size: is the number of items per page.
   * - query: is the string that should be matched.
   */
  searchBusinessByQueryByReseller(payload: { page: number, size: number, query: string, resellerId: string }) {
    const request: RequestModel = {
      params: `resellers/businesses/autocomplete?page=${payload.page}&size=${payload.size}&query=${payload.query}&id=${payload.resellerId}`
    }
    this.store.dispatch(BusinessActions.searchBusinessesByQuery({ request }))
  }

  /**
   * Dispatches the action to get all app users and their business/reseller information,
   * that matches a specific string
   *  <pre>
     * <code class="language-typescript">
     * searchUserBusinessByQuery({page: 0, size: 30, query: 'John'})
        </code>
    * </pre>
   * @param payload Contains the properties to be sent
   * to the endpoint, where: 
   * - page: is the page numbern.
   * - size: is the number of items per page.
   * - query: is the string that should be matched.
   */
  searchUserBusinessByQuery(payload: { page: number, size: number, query: string }) {
    const request: RequestModel = {
      params: `userBusiness/autocomplete?query=${payload.query}&page=${payload.page}&size=${payload.size}`
    }
    this.store.dispatch(UsersActions.searchAllUsers({ request }))
  }

  /**
   * Dispatches the action to search a reseller for
   * its unique id.
   * <pre>
     * <code class="language-typescript">
     * selectedBusinessProfile('5e8388ba69254e3b7e931f3e')
        </code>
    * </pre>
   * @param resellerId The unique reseller id.
   */
  searchResellerByBusiness(resellerId: string) {
    const request: RequestModel = {
      params: `resellers/${resellerId}`
    }
    this.store.dispatch(ResellersActions.searchResellerByBusiness({ request }))
  }

  /**
   * Dispatches the action to search all users that belng
   * to an specific business
   * <pre>
     * <code class="language-typescript">
     * searchAllUsers({page: 0, size: 9})
        </code>
    * </pre>
   * @param data Contains the properties to be sent
   * to the endpoint, where: 
   * - page: is the page number.
   * - size: is the number of items per page.
   */
  searchAllUsers(data: { page: number, size: number }) {
    const request: RequestModel = {
      params: `userBusiness?page=${data.page}&size=${data.size}`
    }
    this.store.dispatch(UsersActions.searchAllUsers({ request }))
  }

  /**
   * Dispatches the action to search users by reseller.
   * The search is performed as a paginated search.
   * @param data Contains the properties to be sent
   * to the endpoint, where: 
   * <pre>
     * <code class="language-typescript">
     * searchUsersByReseller({page: 0, size: 9, resellerId: '5e8388ba69254e3b7e931f3e'})
        </code>
    * </pre>
   * - resellerId: is the unique id of the reseller to which the users belongs to.
   * - page: is the page number .
   * - size: is the number of items per page.
   */
  searchUsersByReseller(data: { page: number, size: number, resellerId: string }) {
    const request: RequestModel = {
      params: `resellers/users?id=${data.resellerId}&page=${data.page}&size=${data.size}`,
    }
    this.store.dispatch(UsersActions.searchUsersByReseller({ request }))
  }

  /**
   * Dispatches the action to search users by reseller and that match a string.
   * The search is performed as a paginated search.
   * <pre>
     * <code class="language-typescript">
     * searchUsersQueyByReseller({page: 0, size: 9, resellerId: '5e8388ba69254e3b7e931f3e', q: 'John'})
        </code>
    * </pre>
   * @param data Contains the properties to be sent
   * to the endpoint, where: 
   * - resellerId: is the unique id of the reseller to which the users belongs to.
   * - page: is the page number.
   * - size: is the number of items per page.
   * - q: is the string to be matched.
   */
  searchUsersQueyByReseller(data: { page: number, size: number, resellerId: string, q: string }) {
    const request: RequestModel = {
      params: `resellers/users/autocomplete?id=${data.resellerId}&page=${data.page}&size=${data.size}&query=${data.q}`,
    }
    this.store.dispatch(UsersActions.searchUsersByReseller({ request }))
  }

  /** Dispatches the action to request the app user roles 
   * <pre>
     * <code class="language-typescript">
     * searchUserRoles()
        </code>
    * </pre>
  */
  searchUserRoles() {
    this.store.dispatch(UsersActions.searchUserRoles())
  }

  /**
   * Dispatches the action to  log all
   * API request to the data base throught the API.
   * <pre>
     * <code class="language-typescript">
     * loggerRequest(
     * request: ''),
     * response: "{"version":"1.0.115","type":"optional"}",
     * url: "https://prod2.api.plsrvw.net/version.json",
     * version: "2_VERBOSE"
     * )
        </code>
    * </pre>
   * @param payload The API request to be logged.
   */
  loggerRequest(payload: any) {
    const request: RequestModel = {
      params: `maintenance/logger`,
      payload
    }
    this.store.dispatch(AppActions.loggerRequest({ request }))
  }

  saveCognitoErrors(payload: any) {
    const request: RequestModel = {
      params: `cognito-log`,
      payload
    }
    this.store.dispatch(AppActions.saveCognitoError({ request }))
  }

  // SELECT GETTERS
  /**
   * Gets the current loggued user
   * @returns An observable of UserModel with the information of the logged user
   */
  getLoggedUser$(): Observable<UserModel> {
    return this.store.select(fromRoot.getLoggedUser).pipe(
      tap(({ email }) => { if (email) startProfitwell(email) })
    );
  }

  /**
   * Gets the role of current logged user
   * @returns An observable of string that contains the role of the logged user
   */
  getRoleLoggedUser$(): Observable<Role> {
    return this.store.select(fromRoot.getRoleLoggedUser)
  }

  /**
   * Gets the businesses that are stored on the app state.
   * @returns An observable of BusinessModel array with the list of businesses
   */
  getBusinesses$(): Observable<BusinessModel[]> {
    return this.store
      .select(fromRoot.getBusinesses)
      .pipe(
        map(items => items
          .map(addRandomImageToBusiness)
          .sort((a: BusinessModel, b: BusinessModel) => a.name.localeCompare(b.name))
        ),
      )
  }

  /**
   * Gets the app selected business
   * @returns An observable of BusinessModel with the information of the selected business
   */
  getSelectedBusiness$(): Observable<BusinessModel> {
    return this.store.select(fromRoot.getSelectedBusinessProfile)
  }

  /**
   * Gets the app state property that indicates
   * if there is a request in progress or not.
   * @returns An observable of a boolean indicating whether a task is 
   * in progress or not.
   */
  getIsTaskExecuting$(): Observable<boolean> {
    return this.store.select(fromRoot.getIsExecutedTaskBusiness)
  }

  /**
   * Gets the total of business that are contained on
   * the businesses array.
   * @returns An observable that contains the total of businesses
   */
  getBusinessTotal$(): Observable<number> {
    return this.store.select(fromRoot.getTotalBusinesses)
  }

  /**
   * Gets the user list that is stored on the app state.
   * @returns An observable of an user array
   */
  getUsers$(): Observable<UserBusinessModel[]> {
    return this.store.select(fromRoot.getUsers).pipe(
      // map(users => users.map(user => ({ ...user, reseller: "John Doe Reviews" })))
    );
  }

  /**
   * Gets the app user roles 
   * @returns An observable of user roles
   */
  getUserRoles$(): Observable<UserRolesModel[]> {
    return this.store.select(fromRoot.getUserRoles)
  }

  /**
   * Gets the names of selected Reseller, Business and Location.
   * @returns An observable with the information of reseller name, business name and location name
   */
  getResellerBusinessLocation$() {
    return this.store.select(fromRoot.getResellerBusinessLocation)
  }

  /**
   * Gets information on whether a task is being executed or not
   * @returns An observable that contains if a task is being
   * excecuted or not.
   */
  isTaskExecuting$(): Observable<boolean> {
    return this.store.select(fromRoot.getIsTaskExecuting)
  }

  /**
   * Gets the state that indicates if the drawer that
   * contains the main menu is being showed or not.
   * @returns An observable that contains if the drawer is showed.
   */
  openSideDrawer$() {
    return this.store.select(fromRoot.openSideDrawer)
  }

  /**
   * Gets the last app notification
   * @returns An observable of the last notification
   */
  getLastNotification$() {
    return this.store.select(fromRoot.getLastNotification)
  }


  /**
   * Gets the status that indicates if intercom widget should be shown
   * or not.
   * @returns An observable of a boolean that indicates if the intercom
   * widget should be shown for the current user session.
   */
  getIsShowIntercom$(): Observable<boolean> {
    return this.store.select(fromRoot.getIsShowIntercom)
  }

  /**
   * Gets the information about whether the user should be
   * logged out due to inactivity or not.
   * @returns An observable of user inactivity status
   */
  getErrorTimeInactive$(): Observable<boolean> {
    return this.store.select(fromRoot.getErrorTimeInactive)
  }

  /** Dispatches the action to update authentication tokens
   * <pre>
     * <code class="language-typescript">
     * updateToken()
        </code>
    * </pre>
  */
  updateToken() {
    this.store.dispatch(AuthActions.updateToken())
  }

  /** 
   * Dispatches the action to set the last app notification.
   * <pre>
     * <code class="language-typescript">
     * setLastNotification({ id: '5e8388bb69254e3b7e9327eb', type: 'BUSINESS_DELETE', dateCreated: 1600804809 })
        </code>
    * </pre>
   * @param notification the last notification to be stored.
   * */
  setLastNotification(notification) {
    this.store.dispatch(AppActions.setLastNotification({ notification }))
  }

  /** 
   * Dispatches the action to set if the logged user should see intercom or not.
   * <pre>
     * <code class="language-typescript">
     * isShowIntercom(true)
        </code>
    * </pre>
   * @param show a boolean that indicates if intercom widget should be shown or not
   * */
  isShowIntercom(show: boolean) {
    this.store.dispatch(AppActions.isShowIntercom({ show }))
  }

  /** 
   * Dispatches the action to open side menu
   * <pre>
     * <code class="language-typescript">
     * openSideBar()
        </code>
    * </pre>
    */
  openSideBar() {
    this.store.dispatch(AppActions.openSideBar())
  }

  /** 
   * Dispatches the action to set the time passed since the last user activity was detected
   * on the app state.
   * <pre>
     * <code class="language-typescript">
     * setLastTimeActive()
        </code>
    * </pre>
   *  */
  setLastTimeActive() {
    this.store.dispatch(AppActions.setLastTimeActive())
  }

  /** 
   * Dispatches the action to set whether the inactivity time has been met or not.
   * <pre>
     * <code class="language-typescript">
     * setErrorTimeInactive(true)
        </code>
    * </pre>
   * @param errorTimeInactive a boolean that indicates if inactivity time has been met or not
   */
  setErrorTimeInactive(errorTimeInactive: boolean) {
    this.store.dispatch(AppActions.setErrorTimeInactive({ errorTimeInactive }))
  }

  getRedirectedFromGoogle$(): Observable<boolean> {
    return this.store.select(fromRoot.getRedirectedFromGoogle)
  }

  setRedirectedFromGoogle(redirected: boolean){
    this.store.dispatch(AppActions.setRedirectedFromGoogle({ redirected }))
  }

  submitDataDeletionRequest(payload: { dataDeletionRequest: DataDeletionRequestModel }) {
    const request: RequestModel = {
        params: `facebook/delete-account`,
        payload: payload.dataDeletionRequest
    }
    this.store.dispatch(AppActions.submitDataDeletionRequest({ request }))
}

}
